@if (detail(); as d) {
  <app-page-header
    eyebrow="Профил на пазара"
    [title]="'Цени на имоти в ' + d.city.name"
    [lead]="'Регион ' + d.city.region + ' · население ' + fmtInt(d.city.population) + ' · пълният набор показатели за продажби и наеми.'"
  >
    <app-range-toggle [value]="rangeMonths()" (change)="rangeMonths.set($event)" />
    <app-granularity-toggle [value]="granularity()" (change)="granularity.set($event)" />
  </app-page-header>

  <app-market-notice />

  <div class="grid cols-4">
    <app-kpi-card
      label="Медиана €/м² (продажба)"
      [value]="fmtEurPerM2(d.price.headline)"
      badge="Офертни цени"
      sub="обявени от продавачи"
      [mom]="d.price.momPct"
      [yoy]="d.price.yoyPct"
    />
    <app-kpi-card
      label="Медиана наем €/м²/мес"
      [value]="fmt2(d.rent.headline) + ' €'"
      [mom]="d.rent.momPct"
      [yoy]="d.rent.yoyPct"
    />
    <app-kpi-card
      label="Брутна доходност"
      [value]="fmt2(d.rentalYield.headline) + ' %'"
      [yoy]="d.rentalYield.yoyPct"
    />
    <app-kpi-card
      label="Активни обяви"
      [value]="fmtInt(d.inventory.headline)"
      [mom]="d.inventory.momPct"
      [yoy]="d.inventory.yoyPct"
    />
  </div>

  <div class="grid cols-2 charts-row">
    <div class="card">
      <h3>Цена срещу обем на сделките</h3>
      <app-trend-chart
        [data]="priceChartSeries(d)"
        [dualAxis]="true"
        ariaLabel="Цена срещу брой сключени сделки — разминаването е водещ индикатор"
      />
    </div>
    <div class="card">
      <h3>Наем €/м²/мес — тенденция</h3>
      <app-trend-chart [data]="[{ name: 'Наем €/м²', series: ranged(d.rent.series) }]" unit="€/м²" />
    </div>
  </div>

  <h2>Сделки и финансиране</h2>
  <div class="grid cols-4">
    <app-kpi-card
      label="Сделки / тримесечие"
      [value]="fmtInt(d.transactions.headline)"
      [badge]="d.transactions.isInProgress ? 'в ход' : null"
      [sub]="d.transactions.progressSub ?? 'вписани сделки (Агенция по вписванията)'"
      [mom]="d.transactions.momPct"
      [yoy]="d.transactions.yoyPct"
    />
    <app-kpi-card
      label="Лихва ⇄ обем сделки"
      [value]="corrLabel(d.rateVolumeCorrelation.direction)"
      [sub]="'корелация на Δ за 12 мес.: ' + (d.rateVolumeCorrelation.coef ?? '–')"
    />
  </div>

  <app-mortgage-calc
    [medianPriceEurPerM2]="d.price.headline"
    [defaultInterestRate]="benchmarkMortgageRate()"
    [cityName]="d.city.name"
  />

  <h2>Здраве на пазара</h2>
  <div class="grid cols-4">
    <app-kpi-card
      label="Дял намалени обяви"
      [value]="fmt1(d.priceCutRate.headline) + ' %'"
      [sub]="'среден дисконт ' + fmt1(d.avgDiscountPct) + '%'"
      [yoy]="d.priceCutRate.yoyPct"
      [invert]="true"
    />
    <app-kpi-card
      label="Месеци до изчерпване"
      [value]="fmt1(d.absorptionMonths)"
      sub="наличност ÷ средни продажби (6 мес.)"
    />
    <app-kpi-card
      label="Дни на пазара (DOM)"
      [value]="fmtInt(d.domDays)"
      sub="медиана, свалени обяви (12 мес.)"
    />
    <app-kpi-card
      label="Достъпност (PIR, градски доход)"
      [value]="fmt1(d.pir.headline) + ' год.'"
      sub="медианен имот ÷ градски годишен доход"
      [yoy]="d.pir.yoyPct"
      [invert]="true"
    />
    <app-kpi-card
      label="Изплащане чрез наем (P/R)"
      [value]="fmt1(d.priceToRent.headline) + ' год.'"
      sub="цена ÷ годишен брутен наем"
      [yoy]="d.priceToRent.yoyPct"
      [invert]="true"
    />
  </div>

  <div class="grid cols-2 charts-row">
    <div class="card">
      <h3>Дял намалени обяви (%)</h3>
      <app-trend-chart [data]="[{ name: '% намалени', series: ranged(d.priceCutRate.series) }]" unit="%" />
    </div>
    <div class="card">
      <h3>Достъпност (години доход)</h3>
      <app-trend-chart
        [data]="[
          { name: 'PIR (градски доход)', series: ranged(d.pir.series) },
          { name: 'Регионален доход', series: ranged(d.affordability.series) },
        ]"
        unit="год."
      />
    </div>
  </div>

  <h2>Прегряване и инерция</h2>
  <div class="grid cols-3">
    <div class="card gauge-card">
      <h3>Индекс на прегряване</h3>
      <app-gauge-chart [score]="d.overheating.score" />
      <p class="note">
        Цени {{ d.overheating.priceYoY | pct }} ГоГ · доходи {{ d.overheating.incomeYoY | pct }} ·
        наеми {{ d.overheating.rentYoY | pct }}
      </p>
    </div>
    @if (d.momentum !== null) {
      <app-kpi-card
        label="Инерция на цените"
        [value]="(d.momentum > 0 ? '+' : '') + fmt2(d.momentum) + ' пп/мес'"
        [sub]="
          d.momentum > 0.02
            ? 'ръстът се ускорява'
            : d.momentum < -0.02
              ? 'ръстът се забавя'
              : 'стабилен темп'
        "
      />
    } @else {
      <app-kpi-card label="Инерция на цените" value="—" sub="нужни са поне 8 месеца история" />
    }
    <app-kpi-card
      label="Градиент от центъра"
      [value]="fmt1(d.centerGradient.decayPctPerKm) + ' %/км'"
      sub="промяна на €/м² с всеки км от центъра"
    />
  </div>

  <div class="section-head">
    <div class="title-with-count">
      <h2>Квартали</h2>
      <span class="muted count-pill">
        {{ filteredNeighborhoods().length }} от {{ d.neighborhoods.length }}
      </span>
    </div>
    <div class="section-actions">
      <div class="search-input-wrap">
        <input
          type="search"
          class="search-input"
          placeholder="Търси квартал…"
          [value]="nbhdQuery()"
          (input)="nbhdQuery.set($any($event.target).value)"
          aria-label="Филтрирай квартали по име"
        />
        @if (nbhdQuery()) {
          <button type="button" class="clear-btn" (click)="nbhdQuery.set('')" aria-label="Изчисти търсенето">✕</button>
        }
      </div>
      <div class="view-toggle" role="group" aria-label="Изглед на кварталите">
        <button type="button" [class.active]="nbhdView() === 'table'" (click)="nbhdView.set('table')">
          Таблица
        </button>
        <button type="button" [class.active]="nbhdView() === 'map'" (click)="nbhdView.set('map')">
          Карта
        </button>
      </div>
    </div>
  </div>
  @if (nbhdView() === 'table') {
    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th class="sortable" (click)="sortNbhd('name')">Квартал{{ nbhdArrow('name') }}</th>
            <th class="num sortable" (click)="sortNbhd('distance')">км от центъра{{ nbhdArrow('distance') }}</th>
            <th class="num sortable" (click)="sortNbhd('price')">Продажба €/м²{{ nbhdArrow('price') }}</th>
            <th class="num sortable" (click)="sortNbhd('rent')">Наем €/м²{{ nbhdArrow('rent') }}</th>
            <th class="num sortable" (click)="sortNbhd('yield')">Доходност{{ nbhdArrow('yield') }}</th>
            <th class="num sortable" (click)="sortNbhd('yoy')">ГоГ{{ nbhdArrow('yoy') }}</th>
            <th class="num sortable" (click)="sortNbhd('listings')">Обяви{{ nbhdArrow('listings') }}</th>
          </tr>
        </thead>
        <tbody>
          @for (n of filteredNeighborhoods(); track n.neighborhoodId) {
            <tr>
              <td class="strong">{{ n.name }}</td>
              <td class="num muted">{{ n.distanceFromCenterKm | bg1 }}</td>
              <td class="num strong">{{ n.medianSaleEurPerM2 | eurM2 }}</td>
              <td class="num">{{ n.medianRentEurPerM2 | bg2 }} €</td>
              <td class="num">{{ n.rentalYieldPct | bg2 }} %</td>
              <td class="num"><app-delta-chip [value]="n.yoyPct" label="ГоГ" /></td>
              <td class="num">{{ n.activeListings | bgInt }}</td>
            </tr>
          } @empty {
            <tr>
              <td colspan="7" class="empty-msg">Няма открити квартали по зададеното търсене „{{ nbhdQuery() }}“.</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  } @else {
    <div class="card">
      <app-neighborhood-map [data]="filteredNeighborhoods()" metric="price" [ariaLabel]="'Карта на кварталите в ' + d.city.name" />
    </div>
  }

  <h2>Срезове на пазара</h2>
  <div class="grid cols-3">
    <div class="card">
      <h3>По тип имот</h3>
      @for (row of d.byType; track row.key) {
        <div class="mini-row">
          <span>{{ typeLabel(row) }}</span>
          <span class="mini-val">{{ row.medianEurPerM2 | eurM2 }}</span>
          <app-delta-chip [value]="row.yoyPct" label="ГоГ" />
        </div>
      }
    </div>
    <div class="card">
      <h3>По строителство</h3>
      @for (row of d.byConstruction; track row.key) {
        <div class="mini-row">
          <span>{{ constructionLabel(row) }}</span>
          <span class="mini-val">{{ row.medianEurPerM2 | eurM2 }}</span>
          <app-delta-chip [value]="row.yoyPct" label="ГоГ" />
        </div>
      }
    </div>
    <div class="card">
      <h3>По година на строеж</h3>
      @for (row of d.byBuildYearBand; track row.key) {
        <div class="mini-row">
          <span>{{ bandLabel(row) }}</span>
          <span class="mini-val">{{ row.medianEurPerM2 | eurM2 }}</span>
          <app-delta-chip [value]="row.yoyPct" label="ГоГ" />
        </div>
      }
    </div>
  </div>

  @if (faqItems().length > 0) {
    <section class="city-faq">
      <h2>Често задавани въпроси за имотите в {{ d.city.name }}</h2>
      <div class="faq-list">
        @for (item of faqItems(); track item.q) {
          <details class="faq-card card">
            <summary class="faq-question">{{ item.q }}</summary>
            <p class="faq-answer">{{ item.a }}</p>
          </details>
        }
      </div>
    </section>
  }

  <p class="cta">
    <a class="btn" [routerLink]="['/obiavi']" [queryParams]="{ city: d.city.slug }">
      Виж обявите в {{ d.city.name }} →
    </a>
  </p>
} @else {
  <p class="loading">Зареждане на профила…</p>
}
